import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { USER_ROLES, INV_RELATED_ROLES } from "../../utils/constants";
import CustomButton from "../UI/CustomButton";
import SaveForFollowingVisitsCheckbox, {
  canSaveForFollowingVisits,
} from "./SaveForFollowingVisitsCheckbox";
import useUpdateVisitInstructionsMutation from "../../hooks/useUpdateVisitInstructionsMutation";
import useGetVisitInstructions from "../../hooks/useGetVisitInstructions";

const EXTRA_LANGUAGES = [
  { key: "de", label: "German (DE)" },
  { key: "es", label: "Spanish (ES)" },
];

const EMPTY_INSTRUCTIONS = {
  contnt: "",
  contnt_de: "",
  contnt_es: "",
  enabled_de: false,
  enabled_es: false,
};

const InstructionsModule = ({
  role,
  trial,
  selectedSessionId,
  selectedVisit,
  patientId,
  visitId,
  step,
  isTrialLocked = false,
}) => {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [local, setLocal] = useState(EMPTY_INSTRUCTIONS);
  const [localIsAllInstruction, setLocalIsAllInstruction] = useState(false);
  const [localDisallowInv, setLocalDisallowInv] = useState(false);

  const stepId = step?.id;
  const label = step?.title || "Instructions";
  const testIdSuffix = stepId ? `-${stepId}` : "";

  const updateMutation = useUpdateVisitInstructionsMutation();

  // Step-scoped truth from the backend — always fetched (not just in patient mode) so a
  // repeated module's CRO view reads this step's own content instead of the flat,
  // union-of-all-steps `selectedVisit.visit_instructions` field.
  const { data: visitInstructionsData } = useGetVisitInstructions({
    visitId,
    patientId,
    sessionId: selectedSessionId,
    stepId,
  });

  const isInvestigatorRelated = INV_RELATED_ROLES.includes(role);

  const canView =
    role === USER_ROLES.CRO ||
    role === USER_ROLES.INVESTIGATOR ||
    role === USER_ROLES.SUB_INVESTIGATOR ||
    role === USER_ROLES.CRO_COORDINATOR ||
    role === USER_ROLES.DATA_MANAGER ||
    role === USER_ROLES.CRC ||
    role === USER_ROLES.SUPER_ADMIN;

  const isReadOnly =
    isTrialLocked ||
    ((selectedVisit?.visit_number === -1 || selectedVisit?.visit_number === -2) &&
      (role === USER_ROLES.INVESTIGATOR ||
        role === USER_ROLES.CRC ||
        role === USER_ROLES.SUB_INVESTIGATOR ||
        patientId)) ||
    (trial && !trial?.can_edit_instructions && isInvestigatorRelated) ||
    (patientId && selectedVisit?.disallow_inv_instructions_selection);

  // Adverse Event visits must always keep the investigator locked out of picking
  // instructions — CRO can't turn this off.
  const isAdverseEventVisit = selectedVisit?.visit_number === -1;

  // In patient mode, only an explicit session selection counts as "on" — instruction text
  // merely inherited from the visit is not something the investigator actually wrote yet.
  // Exception: when the investigator is locked out of picking (disallow_inv), there will
  // never be an explicit session selection, so the visit's forced content counts as "on" instead.
  useEffect(() => {
    if (!patientId) {
      if (visitInstructionsData?.visit_instructions?.contnt) {
        setEnabled(true);
      }
    } else if (
      (visitInstructionsData?.is_session_selection ||
        visitInstructionsData?.disallow_inv_instructions_selection) &&
      visitInstructionsData?.visit_instructions?.contnt
    ) {
      setEnabled(true);
    }
  }, [patientId, visitInstructionsData]);

  useEffect(() => {
    if (open) {
      const vi = !patientId
        ? visitInstructionsData?.visit_instructions || {}
        : visitInstructionsData?.is_session_selection ||
            visitInstructionsData?.disallow_inv_instructions_selection
          ? visitInstructionsData?.visit_instructions || {}
          : {};
      setLocal({
        contnt: vi.contnt || "",
        contnt_de: vi.contnt_de || "",
        contnt_es: vi.contnt_es || "",
        enabled_de: vi.enabled_de ?? !!vi.contnt_de,
        enabled_es: vi.enabled_es ?? !!vi.contnt_es,
      });
      setLocalIsAllInstruction(!!visitInstructionsData?.is_all_instruction);
      setLocalDisallowInv(
        isAdverseEventVisit || !!visitInstructionsData?.disallow_inv_instructions_selection
      );
    }
  }, [open, patientId, visitInstructionsData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleEnabled = (e) => {
    const checked = e.target.checked;
    setEnabled(checked);
    if (!checked) {
      updateMutation.mutate({
        visitId,
        patientId,
        sessionId: selectedSessionId,
        stepId,
        contnt: "",
        contntDe: "",
        contntEs: "",
        isAllInstruction: false,
      });
    }
  };

  const handleDisableInvSelection = () => {
    setLocalDisallowInv((prev) => !prev);
  };

  const handleLangToggle = (lang, enabled) => {
    setLocal((prev) => ({
      ...prev,
      ...(!enabled && { [`contnt_${lang}`]: "" }),
      [`enabled_${lang}`]: enabled,
    }));
  };

  const handleSave = () => {
    updateMutation.mutate(
      {
        visitId,
        patientId,
        sessionId: selectedSessionId,
        stepId,
        contnt: local.contnt,
        contntDe: local.enabled_de ? local.contnt_de : "",
        contntEs: local.enabled_es ? local.contnt_es : "",
        isAllInstruction: localIsAllInstruction,
        disallowInvInstructionsSelection: localDisallowInv,
      },
      { onSuccess: () => setOpen(false) }
    );
  };

  if (!canView) return null;

  // Investigators are not shown modules the CRO did not configure for this visit
  if (
    isInvestigatorRelated &&
    patientId &&
    selectedVisit?.visit_number >= 0 &&
    visitInstructionsData?.visit_configured === false
  ) {
    return null;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Checkbox
            inputProps={{ "data-testid": `instructions-enable-checkbox-input${testIdSuffix}` }}
            checked={enabled}
            onChange={handleToggleEnabled}
            disabled={isReadOnly || updateMutation.isPending}
          />
          <Typography variant="subtitle1" fontWeight={600}>
            {label}
          </Typography>
        </Stack>
        {enabled && (
          <CustomButton
            size="small"
            variant="outlined"
            data-testid={`instructions-manage-btn${testIdSuffix}`}
            onClick={() => setOpen(true)}
          >
            {isReadOnly ? "View" : "Manage"}
          </CustomButton>
        )}
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isReadOnly ? "View" : "Manage"} {label}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={1}>
            {!isReadOnly &&
              (role === USER_ROLES.CRO ||
                role === USER_ROLES.CRO_COORDINATOR ||
                role === USER_ROLES.DATA_MANAGER ||
                role === USER_ROLES.SUPER_ADMIN) && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={localDisallowInv}
                      onChange={handleDisableInvSelection}
                      disabled={isAdverseEventVisit}
                    />
                    <Typography variant="body2">
                      Disable instructions selection for investigator
                      {isAdverseEventVisit && " (always enabled for Adverse Event visits)"}
                    </Typography>
                  </Stack>

                  {canSaveForFollowingVisits(selectedVisit) && (
                    <SaveForFollowingVisitsCheckbox
                      label="Save instructions for all coming visits"
                      checked={localIsAllInstruction}
                      onChange={() => setLocalIsAllInstruction((prev) => !prev)}
                    />
                  )}
                </Box>
              )}

            <TextField
              multiline
              rows={3}
              fullWidth
              value={local.contnt}
              onChange={(e) => setLocal((prev) => ({ ...prev, contnt: e.target.value }))}
              disabled={isReadOnly}
            />

            {!isReadOnly &&
              EXTRA_LANGUAGES.map(({ key, label }) => (
                <Box key={key}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={local[`enabled_${key}`]}
                        onChange={(e) => handleLangToggle(key, e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">{label}</Typography>}
                  />
                  {local[`enabled_${key}`] && (
                    <TextField
                      multiline
                      rows={2}
                      fullWidth
                      value={local[`contnt_${key}`]}
                      onChange={(e) =>
                        setLocal((prev) => ({ ...prev, [`contnt_${key}`]: e.target.value }))
                      }
                      placeholder={`Instructions (${key.toUpperCase()})`}
                    />
                  )}
                </Box>
              ))}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          {!isReadOnly && (
            <Button variant="contained" onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstructionsModule;
